// Shared timer state for the mobile timer flow + the desktop timer widget
// (WI-11828). The same localStorage entry backs both views — starting on the
// phone keeps ticking on a wider browser tab, and stopping on either side
// applies to both.
//
// useTimerActions() wraps the start / stop / resume / persist side-effects so
// the mobile page, the desktop bar, and the entries panel all share one
// implementation (no drift on which entry gets created / patched on stop).

import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import type { TimeEntry } from '@/types/api'

export interface TaskRef {
  customerId:   number | string
  customerName: string
  projectId:    number
  projectName:  string
  phaseId:      number
  phaseName:    string
  activityId:   number
  activityName: string
}

export interface RunningTask {
  task:      TaskRef
  startedAt: number
  /** When set, the running timer is resuming a previously-stopped entry.
   *  Persisting will PATCH that entry's workedHrs instead of POSTing a new
   *  one (WI-11928). startedAt is shifted backwards by the prior elapsed so
   *  the live display shows cumulative time. */
  entryId?:  number
}

export interface TimerStateValue {
  running:         RunningTask | null
  recents:         TaskRef[]
  scope:           'mine' | 'all'
  keepAwake:       boolean
  /** Ids of time entries that were created or updated by the timer (and
   *  not yet superseded by a manual edit). Drives the Resume button on
   *  each row (WI-11928). Capped to the most recent N to avoid unbounded
   *  growth — older ids fall off but the entry stays editable as normal. */
  timerEntryIds:   number[]
}

const STORE_KEY = 'forge-mobile-time-v1'
const TIMER_ENTRY_CAP = 100

function defaults(): TimerStateValue {
  return { running: null, recents: [], scope: 'mine', keepAwake: true, timerEntryIds: [] }
}

export function loadTimerState(): TimerStateValue {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (raw) return { ...defaults(), ...JSON.parse(raw) }
  } catch { /* ignore corrupt localStorage */ }
  return defaults()
}

function saveTimerState(s: TimerStateValue) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(s)) } catch { /* ignore */ }
  // Notify other components in the same tab. localStorage 'storage' event
  // only fires across tabs, so we dispatch our own for siblings.
  try {
    window.dispatchEvent(new CustomEvent('forge-timer-update', { detail: s }))
  } catch { /* ignore */ }
}

export function taskKey(t: TaskRef): string {
  return `${t.projectId}::${t.phaseId}::${t.activityId}`
}

/** Build a TaskRef from a TimeEntry. Returns null for entries that can't be
 *  resumed (leave entries, or any row missing project / phase / activity). */
export function taskRefFromEntry(e: TimeEntry): TaskRef | null {
  if (e.projectId == null || e.phaseId == null || e.activityId == null) return null
  if (e.customerId == null) return null
  return {
    customerId:   e.customerId,
    customerName: e.customerName ?? '',
    projectId:    e.projectId,
    projectName:  e.projectName ?? '',
    phaseId:      e.phaseId,
    phaseName:    e.phaseName ?? '',
    activityId:   e.activityId,
    activityName: e.activityName ?? '',
  }
}

// Subscribe to the timer state. Returns [state, setState]. setState writes
// through to localStorage and notifies siblings/other tabs.
export function useTimerState(): [TimerStateValue, (next: TimerStateValue | ((prev: TimerStateValue) => TimerStateValue)) => void] {
  const [state, setState] = useState<TimerStateValue>(loadTimerState)

  // Keep the in-memory copy fresh when other components/tabs write.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORE_KEY) return
      setState(loadTimerState())
    }
    const onCustom = (e: Event) => {
      const ce = e as CustomEvent<TimerStateValue>
      if (ce.detail) setState(ce.detail)
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener('forge-timer-update', onCustom)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('forge-timer-update', onCustom)
    }
  }, [])

  const update = (next: TimerStateValue | ((prev: TimerStateValue) => TimerStateValue)) => {
    setState(prev => {
      const v = typeof next === 'function' ? (next as (p: TimerStateValue) => TimerStateValue)(prev) : next
      saveTimerState(v)
      return v
    })
  }

  return [state, update]
}

// Drives a re-render every second while a timer is running so callers can
// display elapsed time without each one wiring up its own setInterval.
export function useLiveTick(running: boolean, intervalMs = 1000): number {
  const [tick, setTick] = useState(Date.now())
  useEffect(() => {
    if (!running) return
    const id = setInterval(() => setTick(Date.now()), intervalMs)
    return () => clearInterval(id)
  }, [running, intervalMs])
  return tick
}

// ── Display helpers ──────────────────────────────────────────────────────────

function pad(n: number): string { return String(n).padStart(2, '0') }

function toIso(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function fmtDur(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000))
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`
}

export function fmtHours(ms: number): string {
  const h = ms / 3_600_000
  if (h < 1) return `${Math.round(h * 60)}m`
  const hh = Math.floor(h)
  const mm = Math.round((h - hh) * 60)
  return mm === 0 ? `${hh}h` : `${hh}h ${mm}m`
}

export function fmtClock(ts: number): string {
  const d = new Date(ts)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ── Shared actions ──────────────────────────────────────────────────────────
//
// Persisting on stop / switch posts a new time entry for fresh runs, or
// patches the existing one when resuming. The resulting entry id is stored
// on `lastTimerEntry` so the UI knows which row to show the Resume button on.

export function useTimerActions() {
  const queryClient = useQueryClient()
  const [, setState] = useTimerState()

  const persistRunning = useCallback(async (running: RunningTask, endedAt: number) => {
    const elapsed = endedAt - running.startedAt
    if (elapsed <= 1000) return // ignore sub-second flicks
    const workedHrs = Math.round((elapsed / 3_600_000) * 100) / 100
    if (workedHrs <= 0) return

    let entryId: number
    if (running.entryId) {
      await api.patch<{ id: number }>(`/time/entries/${running.entryId}`, {
        workedHrs,
        billableHrs: workedHrs,
      })
      entryId = running.entryId
    } else {
      const res = await api.post<{ id: number }>('/time/entries', {
        projectId:   running.task.projectId,
        phaseId:     running.task.phaseId,
        activityId:  running.task.activityId,
        entryDate:   toIso(new Date(running.startedAt)),
        workedHrs,
        billableHrs: workedHrs,
      })
      entryId = res.id
    }
    setState(prev => {
      const next = [entryId, ...prev.timerEntryIds.filter(id => id !== entryId)]
      return { ...prev, timerEntryIds: next.slice(0, TIMER_ENTRY_CAP) }
    })
    queryClient.invalidateQueries({ queryKey: ['time', 'entries'] })
  }, [queryClient, setState])

  const startTask = useCallback((task: TaskRef) => {
    const now = Date.now()
    setState(prev => {
      if (prev.running) persistRunning(prev.running, now).catch(() => {})
      const k = taskKey(task)
      return {
        ...prev,
        running: { task, startedAt: now },
        recents: [task, ...prev.recents.filter(r => taskKey(r) !== k)].slice(0, 5),
      }
    })
  }, [setState, persistRunning])

  const stopRunning = useCallback(() => {
    setState(prev => {
      if (!prev.running) return prev
      persistRunning(prev.running, Date.now()).catch(() => {})
      return { ...prev, running: null }
    })
  }, [setState, persistRunning])

  const resumeEntry = useCallback((entry: TimeEntry) => {
    const task = taskRefFromEntry(entry)
    if (!task) return
    const priorMs = Math.max(0, entry.workedHrs * 3_600_000)
    const now = Date.now()
    setState(prev => {
      if (prev.running) persistRunning(prev.running, now).catch(() => {})
      const k = taskKey(task)
      return {
        ...prev,
        running:       { task, startedAt: now - priorMs, entryId: entry.id },
        recents:       [task, ...prev.recents.filter(r => taskKey(r) !== k)].slice(0, 5),
        timerEntryIds: prev.timerEntryIds.filter(id => id !== entry.id),
      }
    })
  }, [setState, persistRunning])

  return { persistRunning, startTask, stopRunning, resumeEntry }
}
